import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Col,
  Form,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import { useMutation } from '@tanstack/react-query';
import { initialValues, validationSchema } from './constants';
import { createRiskTravelRequest } from '../../../store/apis/travelService';

function RiskCountryDetails({
  isOpen,
  toggle,
  onCancel,
  reqNo,
  revisionNo,
  onConfirm,
  riskData,
}) {
  const riskExists = riskData && Object.keys(riskData).length > 0;
  const {
    control,
    reset,
    handleSubmit,
    setValue,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  });

  const { mutate: createRiskTravelMutate, isPending } = useMutation({
    mutationFn: async payload => createRiskTravelRequest(payload),
    onSuccess: response => {
      if (response?.flag === 'True') {
        toast.success('Risk country details saved successfully.', {
          className: 'toast-success',
        });
        toggle();
        reset();
      } else if (response?.flag === 'False') {
        toast.error(
          response?.message || 'Failed to save risk country details.',
          {
            className: 'toast-error',
          },
        );
      }
    },
    onError: error => {
      toast.error(
        error?.message ||
          'An error occurred while saving risk country details.',
      );
    },
  });

  useEffect(() => {
    if (riskExists) {
      setValue('travelNecessity', riskData[0]?.TravelNecessity || '');
      setValue('daysStay', riskData[0]?.DaysStay || '');
      setValue('locations', riskData[0]?.Locations || '');
      setValue('precautions', riskData[0]?.Precautions || '');
      setValue('customerContact', riskData[0]?.CustomerContact || '');
    }
  }, [riskData, setValue, riskExists]);

  const onSubmit = async data => {
    const payload = { ...data, reqNo, revisionNo };
    createRiskTravelMutate(payload);
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="md" centered>
      <ModalHeader toggle={toggle}>Risk Country Additional Details</ModalHeader>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody>
          <div className="d-flex flex-column gap-2">
            <div className="d-flex flex-column gap-1">
              <Label>
                Travel Necessity<span className="text-danger">*</span>
              </Label>
              <Controller
                control={control}
                name="travelNecessity"
                render={({ field }) => (
                  <Input
                    {...field}
                    type="text"
                    placeholder="Enter Travel Necessity"
                    className="form-control external-filter-input"
                    readOnly={riskExists}
                  />
                )}
              />
              {errors?.travelNecessity && (
                <span className="text-danger">
                  {errors?.travelNecessity?.message}
                </span>
              )}
            </div>
            <div className="d-flex flex-column gap-1">
              <Label>
                Days Stay<span className="text-danger">*</span>
              </Label>
              <Controller
                control={control}
                name="daysStay"
                render={({ field }) => (
                  <Input
                    {...field}
                    type="text"
                    placeholder="Enter Days Stay"
                    className="form-control external-filter-input"
                    readOnly={riskExists}
                  />
                )}
              />
              {errors?.daysStay && (
                <span className="text-danger">{errors?.daysStay?.message}</span>
              )}
            </div>
            <div className="d-flex flex-column gap-1">
              <Label>
                Location<span className="text-danger">*</span>
              </Label>
              <Controller
                control={control}
                name="locations"
                render={({ field }) => (
                  <Input
                    {...field}
                    type="text"
                    placeholder="Enter Locations"
                    className="form-control external-filter-input"
                    readOnly={riskExists}
                  />
                )}
              />
              {errors?.locations && (
                <span className="text-danger">
                  {errors?.locations?.message}
                </span>
              )}
            </div>
            <div className="d-flex flex-column gap-1">
              <Label>
                Precautions<span className="text-danger">*</span>
              </Label>
              <Controller
                control={control}
                name="precautions"
                render={({ field }) => (
                  <Input
                    {...field}
                    type="text"
                    placeholder="Enter Precautions"
                    className="form-control external-filter-input"
                    readOnly={riskExists}
                  />
                )}
              />
              {errors?.precautions && (
                <span className="text-danger">
                  {errors?.precautions?.message}
                </span>
              )}
            </div>
            <div className="d-flex flex-column gap-1">
              <Label>
                Customer Contact<span className="text-danger">*</span>
              </Label>
              <Controller
                control={control}
                name="customerContact"
                render={({ field }) => (
                  <Input
                    {...field}
                    type="text"
                    placeholder="Enter Customer Contact"
                    className="form-control external-filter-input"
                    readOnly={riskExists}
                  />
                )}
              />
              {errors?.customerContact && (
                <span className="text-danger">
                  {errors?.customerContact?.message}
                </span>
              )}
            </div>
          </div>
        </ModalBody>
        <ModalFooter className="d-flex flex-row gap-2 w-100">
          {riskExists ? (
            <button type="button" className="custom-btn" onClick={toggle}>
              Close
            </button>
          ) : (
            <>
              <Col>
                <button
                  type="submit"
                  className="custom-btn w-100"
                  disabled={!isValid}
                  onClick={onConfirm}
                >
                  {isPending ? <span>Saving..</span> : <span>Save</span>}
                </button>
              </Col>
              <Col>
                <button
                  type="button"
                  className="custom-danger-btn w-100"
                  onClick={() => {
                    onCancel();
                    reset();
                  }}
                >
                  Cancel
                </button>
              </Col>
            </>
          )}
        </ModalFooter>
      </Form>
    </Modal>
  );
}

export default RiskCountryDetails;
